"use strict"

import scrollIntoViewIfNeeded from "scroll-into-view-if-needed"
import { ChatMessageConfig } from "../chat-message/chat-message.component"
import Validation from "../../model/util/validation"
import AnimalNames from "../../model/domain/animal-names"
import { ElementIntents } from "../../model/domain/element-metadata"
import HelpCenterTemplate from "./help-center.component.html"
import AuthService from "../../services/auth/auth.service"
import ToolbarService from "../../services/toolbar/toolbar.service"
import { Notifications } from "../../services/toolbar/toolbar.service"
import { ABTest } from "../../services/ab-test/ab-test-service"
import CacheService from "../../services/cache/cache.service"
import MediaService from "../../services/media/media.service"
import { StudentHelperService } from "../../../v2/student-helper/services/StudentHelperService"
import { AssignmentExportService } from "../../../v2/student-helper/services/AssignmentExportService"
import { ViewHelps } from "../view-help-dialog/view-help-dialog.controller"

class HelpCenterController {
  /* @ngInject */
  /**
   * @param {AuthService} AuthService
   * @param {ToolbarService} ToolbarService
   * @param {CacheService} CacheService
   * @param {MediaService} MediaService
   * @param {StudentHelperService} StudentHelperService
   * @param {AssignmentExportService} AssignmentExportService
   */
  constructor(
    $scope,
    $timeout,
    $mdDialog,
    AuthService,
    ToolbarService,
    CacheService,
    MediaService,
    StudentHelperService,
    AssignmentExportService,
  ) {
    this.$scope = $scope
    this.$timeout = $timeout
    this.$mdDialog = $mdDialog

    /** @type {AuthService} */
    this._authService = AuthService
    /** @type {ToolbarService} */
    this._toolbarService = ToolbarService
    /** @type {CacheService} */
    this._cacheService = CacheService
    /** @type {MediaService} */
    this._mediaService = MediaService
    /** @type {StudentHelperService} */
    this._studentHelperService = StudentHelperService
    /** @type {AssignmentExportService} */
    this._assignmentExportService = AssignmentExportService

    this._chatMessageConfigs = new Map()
    this.newMessageText = ""
    this.loading = true
    this.isInfoModalVisible = false

    this._showStudentHelper = false
    this._cacheService.getTestSegment(ABTest.StudentHelper).then((show) => {
      this._showStudentHelper = show
    })

    this._cacheService.getUser().then((user) => {
      this._user = user
    })
  }

  async startStudentHelper() {
    const work = this._toolbarService.work
    const question = this._toolbarService.workQuestion
    const correlationId = crypto.randomUUID()

    let timedOut = false
    const handleStudentHelperReferencePicked = async (event) => {
      if (timedOut) return
      if (event.type !== Notifications.STUDENT_HELPER_REFERENCE_PICKED) return
      if (event.data.correlationId !== correlationId) return

      timedOut = true
      this._studentHelperPickedUnsubscribe?.()

      try {
        const blob =
          await this._assignmentExportService.convertCurrentQuestionToBlobWithOverlay(
            {
              x: event.data.x,
              y: event.data.y,
            },
          )
        const mediaLink = await this._mediaService.saveQuestionThumbnail(
          question.id,
          blob,
          ElementIntents.CONTENT,
          question.elementListId,
          question.elementListDb,
        )
        await this._studentHelperService.requestHelper(
          work.assignment.id,
          question.id,
          work.id,
          mediaLink,
          new Date(),
        )
      } catch (err) {
        console.error("Failed to start Student Helper", err)
        // TODO: show error to user
        return
      }
      this.openStudentHelper()
    }

    this._toolbarService.notify(
      Notifications.STUDENT_HELPER_PICK_REFERENCE,
      { correlationId },
      false,
    )

    this._toolbarService.notification.subscribe(
      handleStudentHelperReferencePicked,
      this,
    )
    this._studentHelperPickedUnsubscribe = () => {
      this._toolbarService.notification.unsubscribe(
        handleStudentHelperReferencePicked,
        this,
      )
    }
    // unsubscribe after 30s
    this.$timeout(() => {
      timedOut = true
      this._studentHelperPickedUnsubscribe?.()
      console.log("Canceled Student Helper reference picked")
    }, 30000)
  }

  $postLink() {
    this.resizeTextarea()
    this.markAsRead()
  }

  markAsRead() {
    this._markAsReadTimeout = this.$timeout(() => {
      if (
        this.helpCenter.helpRequestManager.currentUserId !==
          this.helpCenter.helpRequestManager.helpeeId &&
        this.helpCenter.helpRequestManager.isHelpRequestChat
      ) {
        this.helpCenter.helpRequestManager.resolve()
      }
      this.helpCenter.chatMessageManager.markRead()
    }, 1000)
  }

  $onDestroy() {
    if (this._markAsReadTimeout) {
      this.$timeout.cancel(this._markAsReadTimeout)
    }
  }

  resizeTextarea() {
    this.$timeout(() => {
      this.$scope.$broadcast("md-resize-textarea")
      this.loading = false
    }, 400)
  }

  sendMessage() {
    if (!Validation.validateChatMessage(this.newMessageText)) {
      return
    }

    let newMessageText = this.newMessageText
    this.newMessageText = ""
    this.helpCenter.sendMessage(newMessageText).then(() => {
      this.scrollToBottomOfChatMessages()
    })
  }

  scrollToBottomOfChatMessages() {
    let chatMessage = angular.element("chat-message:last-child")[0]

    if (chatMessage) {
      scrollIntoViewIfNeeded(chatMessage, false)
    }
  }

  cancelSidenav() {
    this._toolbarService.sidenavManager.closeState()
  }

  /**
   * @param currentMessage {ChatMessage}
   * @param index {number}
   * @returns {ChatMessageConfig}
   */
  chatMessageConfig(currentMessage) {
    if (
      !this._chatMessageConfigs.has(currentMessage.id) &&
      this._toolbarService.hasTargetIndex
    ) {
      this._chatMessageConfigs.set(
        currentMessage.id,
        new ChatMessageConfig(
          currentMessage,
          this._authService.currentUserId,
          this._authService.authData.roles[0],
          this._toolbarService.target.ownerId,
          this.helpCenter.helpRequestManager.anonAnimalNameForHelpee,
          this.anonAnimalNameForHelper(currentMessage.userId),
          this._toolbarService.target.assignment.ownerId,
        ),
      )
    }

    return this._chatMessageConfigs.get(currentMessage.id)
  }

  /**
   * @param currentMessageUserId {string}
   * @return {string|undefined}
   */
  anonAnimalNameForHelper(currentMessageUserId) {
    let questionId = this._toolbarService.contentQuestion.id
    let animalName = AnimalNames.generate(
      `${questionId}${currentMessageUserId}`,
    )
    return animalName ? animalName.display : ""
  }

  focusOnMessageInput() {
    this.isGlowing = true
    return angular.element("textarea")[1].focus()
  }

  stopGlowAnimation() {
    this.isGlowing = false
  }

  requestForHelp() {
    this.stopGlowAnimation()
    this.helpCenter.newHelpRequest_Help()
  }

  requestForCheck() {
    this.stopGlowAnimation()
    this.helpCenter.newHelpRequest_Check()
  }

  /**
   * @returns {HelpCenterManager}
   */
  get helpCenter() {
    return this._toolbarService.helpCenter
  }

  /**
   * @returns {boolean}
   */
  get requestHelp() {
    return this.options && this.options.requestHelp
  }

  /**
   * @returns {boolean}
   */
  get showStudentHelper() {
    return this._showStudentHelper
  }

  /**
   * @returns {boolean}
   */
  get allowInstantHelper() {
    return this._toolbarService.target.classCode.allowInstantHelper
  }

  /**
   * @returns {boolean}
   */
  get isStudentHelperMode() {
    return this.helpCenterMode === "studentHelper"
  }

  resetHelpCenterModeAndClose() {
    this.helpCenterMode = null
    this._toolbarService.sidenavManager.closeState()
    this._toolbarService.extendedStateUpdated.raise()
  }

  get headerText() {
    return this.isStudentHelperMode ? "Student Helper" : "Help Center"
  }

  openStudentHelper() {
    if (this.helpCenter.helpRequestManager.hasHelpRequest) {
      this.helpCenter.lowerHelpRequest()
    }

    const hasSeenInfoModal = this._user?.getHelpDialogViewed(
      ViewHelps.StudentHelperStudentInfo,
    )

    if (!hasSeenInfoModal && this._user && !this._authService.authData.isAnon) {
      this.isInfoModalVisible = true

      this._user.setHelpDialogViewed(ViewHelps.StudentHelperStudentInfo, true)
      this._cacheService.updateUser(this._user)
    }

    const dataPayload = { mode: "studentHelper" }
    this._toolbarService.sidenavManager.openHelpCenter(dataPayload)
    this.helpCenterMode = "studentHelper"
    this._toolbarService.extendedStateUpdated.raise()
    this.focusOnMessageInput()
  }

  closeInfoModal() {
    this.isInfoModalVisible = false
  }

  /**
   * @returns {boolean}
   */
  get resolveHelp() {
    return this.options && this.options.resolveHelp
  }

  get showHandLoading() {
    return this.helpCenter.helpRequestManager.isHelpRequestLoading
  }

  get showRaiseHand() {
    return (
      this.requestHelp &&
      !this.helpCenter.helpRequestManager.isHelpRequestLoading &&
      !this.helpCenter.helpRequestManager.isHelpRequestCheck &&
      !this.helpCenter.helpRequestManager.isHelpRequestHelp
    )
  }

  get showLowerHand() {
    return (
      (this.resolveHelp || this.requestHelp) &&
      !this.helpCenter.helpRequestManager.isHelpRequestLoading &&
      (this.helpCenter.helpRequestManager.isHelpRequestHelp ||
        this.helpCenter.helpRequestManager.isHelpRequestCheck)
    )
  }

  get showHandInfo() {
    return !this.showHandLoading && !this.showLowerHand && !this.showRaiseHand
  }

  get handInfoMessage() {
    if (
      this.helpCenter.helpRequestManager.isHelpRequestCheck ||
      this.helpCenter.helpRequestManager.isHelpRequestHelp
    ) {
      return "There's a hand up!"
    } else {
      return "No hand raised on this question"
    }
  }

  get noChatsMessage() {
    if (this.isTeacher) {
      return "Your student's hands will appear here!"
    } else {
      return "Your messages will appear here!"
    }
  }

  get isTeacher() {
    return this._authService.authData.isTeacher
  }

  get chatMessageMaxSize() {
    return Validation.ChatMessageMaxSize
  }
}

export default {
  bindings: {
    options: "<",
    helpCenterMode: "=",
  },
  template: HelpCenterTemplate,
  controller: HelpCenterController,
  controllerAs: "ctrl",
}
